import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { OnboardingAnalyticsProvider, useOnboardingAnalyticsContext } from "@/lib/onboarding/OnboardingAnalyticsContext";
import { getOnboardingTotalSteps } from "@/lib/onboarding/screenIds";
import { useOnboardingAnalytics } from "@/lib/onboarding/useOnboardingAnalytics";

import { OnboardingBootHold } from "./OnboardingBootHold";
import { useOnboardingImagesReady } from "./useOnboardingImagePreload";
import { OnboardingShell } from "./OnboardingShell";
import { BenefitsSlide } from "./slides/BenefitsSlide";
import { NarrativeSlide } from "./slides/NarrativeSlide";
import { OverlaySetupSlide, useOverlaySetupSlideAction } from "./slides/OverlaySetupSlide";
import { SetupSlide, useSetupSlideAction } from "./slides/SetupSlide";
import { VpnSetupSlide, useVpnSetupSlideAction } from "./slides/VpnSetupSlide";
import { triggerOnboardingAdvanceHaptic } from "@/lib/native/haptics";

import { setOnboardingCompleted } from "./onboardingStorage";

/** Kept for unused questionnaire slides / future reuse. */
export const PRIMARY_GOAL_IDS = [
  "work",
  "study",
  "sleep",
  "family",
  "hobby",
  "exercise",
  "balance",
  "wellness",
  "other",
] as const;

/** Kept for unused questionnaire slides / future reuse. */
export const TIME_LOST_IDS = [
  "social",
  "news",
  "gaming",
  "streaming",
  "work",
  "shopping",
  "messages",
  "browsing",
  "other",
] as const;

export type PrimaryGoalId = (typeof PRIMARY_GOAL_IDS)[number];
export type TimeLostId = (typeof TIME_LOST_IDS)[number];

type OnboardingFlowProps = {
  onComplete: () => void;
};

function OnboardingFlowInner({ onComplete }: OnboardingFlowProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const [step, setStep] = useState(1);
  const analytics = useOnboardingAnalyticsContext();
  const includeOverlay = Capacitor.getPlatform() === "android";
  const totalSteps = useMemo(() => getOnboardingTotalSteps(includeOverlay), [includeOverlay]);

  /** Screen-time setup is always step 6; overlay (Android) is 7; VPN is last. */
  const setupStep = 6;
  const overlayStep = includeOverlay ? 7 : null;
  const vpnStep = totalSteps;

  const imagesReady = useOnboardingImagesReady();

  useEffect(() => {
    analytics.trackScreenView(step);
  }, [analytics, step]);

  const goNext = useCallback(() => {
    triggerOnboardingAdvanceHaptic();
    setStep((current) => Math.min(current + 1, totalSteps));
  }, [totalSteps]);

  const advance = useCallback(() => {
    analytics.trackCtaClick(step, "primary");
    goNext();
  }, [analytics, goNext, step]);

  const completeOnboarding = useCallback(() => {
    setOnboardingCompleted();
    onComplete();
  }, [onComplete]);

  const completeOnboardingWithHaptic = useCallback(() => {
    triggerOnboardingAdvanceHaptic();
    analytics.trackOnboardingComplete();
    completeOnboarding();
  }, [analytics, completeOnboarding]);

  const completeOnboardingEarly = useCallback(() => {
    analytics.trackOnboardingExit("skip_header", step);
    completeOnboarding();
  }, [analytics, completeOnboarding, step]);

  const { handleSetupCta, ctaLabel: setupCtaLabel, setupBusy } = useSetupSlideAction(goNext, {
    step: setupStep,
  });

  const {
    handleOverlayCta,
    handleSkip: handleOverlaySkip,
    ctaLabel: overlayCtaLabel,
    secondaryCtaLabel: overlaySecondaryCtaLabel,
  } = useOverlaySetupSlideAction(goNext, { step: overlayStep ?? 7 });

  const {
    handleVpnCta,
    handleSkip: handleVpnSkip,
    ctaLabel: vpnCtaLabel,
    secondaryCtaLabel: vpnSecondaryCtaLabel,
    vpnBusy,
    vpnError,
  } = useVpnSetupSlideAction(completeOnboardingWithHaptic, { step: vpnStep });

  let shellCta: { label: string; onClick: () => void; busy?: boolean } | null = null;
  let shellSecondaryCta: { label: string; onClick: () => void } | null = null;

  if (step <= 5) {
    shellCta = { label: t.onboarding.continue, onClick: advance };
  } else if (step === setupStep) {
    shellCta = { label: setupCtaLabel, onClick: () => void handleSetupCta(), busy: setupBusy };
  } else if (overlayStep !== null && step === overlayStep) {
    shellCta = { label: overlayCtaLabel, onClick: () => void handleOverlayCta() };
    shellSecondaryCta = { label: overlaySecondaryCtaLabel, onClick: handleOverlaySkip };
  } else if (step === vpnStep) {
    shellCta = { label: vpnCtaLabel, onClick: () => void handleVpnCta(), busy: vpnBusy };
    shellSecondaryCta = {
      label: vpnSecondaryCtaLabel,
      onClick: handleVpnSkip,
    };
  }

  let content: ReactNode = null;

  if (step === 1) {
    content = (
      <NarrativeSlide
        imageStep={1}
        imageAlt={t.onboarding.narrative1ImageAlt}
        paragraphs={t.onboarding.narrative1}
      />
    );
  } else if (step === 2) {
    content = (
      <NarrativeSlide
        imageStep={2}
        imageAlt={t.onboarding.narrative2ImageAlt}
        paragraphs={t.onboarding.narrative2}
      />
    );
  } else if (step === 3) {
    content = (
      <NarrativeSlide
        imageStep={3}
        imageAlt={t.onboarding.narrative3ImageAlt}
        paragraphs={t.onboarding.narrative3}
      />
    );
  } else if (step === 4) {
    content = (
      <NarrativeSlide
        imageStep={4}
        imageAlt={t.onboarding.narrative4ImageAlt}
        paragraphs={t.onboarding.narrative4}
      />
    );
  } else if (step === 5) {
    content = <BenefitsSlide />;
  } else if (step === setupStep) {
    content = <SetupSlide onAdvance={advance} />;
  } else if (overlayStep !== null && step === overlayStep) {
    content = <OverlaySetupSlide />;
  } else if (step === vpnStep) {
    content = <VpnSetupSlide error={vpnError} />;
  }

  if (!imagesReady) {
    return <OnboardingBootHold />;
  }

  return (
    <OnboardingShell
      contentKey={step}
      showProgress
      progressTotal={totalSteps}
      progressCurrent={step}
      ctaLabel={shellCta?.label}
      ctaBusy={shellCta?.busy}
      onCta={shellCta?.onClick}
      secondaryCtaLabel={shellSecondaryCta?.label}
      onSecondaryCta={shellSecondaryCta?.onClick}
      onSkip={completeOnboardingEarly}
    >
      {content}
    </OnboardingShell>
  );
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const analytics = useOnboardingAnalytics();

  return (
    <OnboardingAnalyticsProvider value={analytics}>
      <OnboardingFlowInner onComplete={onComplete} />
    </OnboardingAnalyticsProvider>
  );
}
